import { useState, type ReactNode } from "react";
import { EDITIONS } from "../app/config.ts";
import { useApp } from "../app/state.tsx";
import { useRoute } from "../lib/router.tsx";

/** Briques du back-office, d'après les motifs « liste » et « formulaire » de la maquette. */

export type Ton = "ok" | "warn" | "danger" | "accent" | "muted";

export type Cellule =
  | { type: "texte"; texte: string }
  | { type: "lien"; texte: string }
  | { type: "muet"; texte: string }
  | { type: "badge"; texte: string; ton: Ton }
  | { type: "bouton"; texte: string };

function Cell({ c }: { c: Cellule }): React.ReactElement {
  switch (c.type) {
    case "lien":
      return <a href="#" onClick={(e) => e.preventDefault()} style={{ fontWeight: 600 }}>{c.texte}</a>;
    case "muet":
      return <span style={{ color: "var(--muted-2)", whiteSpace: "nowrap" }}>{c.texte}</span>;
    case "badge":
      return <span className={`pill pill-${c.ton}`} style={{ textTransform: "none", fontFamily: "var(--sans)", fontSize: 11.5, fontWeight: 700 }}>{c.texte}</span>;
    case "bouton":
      return <button className="btn btn-ghost" style={{ padding: "5px 11px", fontSize: 12 }}>{c.texte}</button>;
    default:
      return <span>{c.texte}</span>;
  }
}

/** Barre de filtre commune (édition + recherche + export + ajouter). */
function BarreFiltre({ ajouter, exportable = true, onAjouter }: { ajouter?: string; exportable?: boolean; onAjouter?: () => void }): React.ReactElement {
  const app = useApp();
  return (
    <div className="card" style={{ display: "flex", alignItems: "center", gap: 11, flexWrap: "wrap", padding: "13px 16px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
        <span className="eyebrow" style={{ color: "var(--faint)" }}>Édition</span>
        <select className="select" style={{ width: "auto", fontWeight: 600, padding: "7px 11px" }} value={app.edition.id} onChange={(e) => void app.changerEdition(Number(e.target.value))}>
          {EDITIONS.map((e) => <option key={e.id} value={e.id}>{e.optionLabel}</option>)}
        </select>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, border: "1px solid var(--line-2)", borderRadius: 9, padding: "7px 11px", minWidth: 250, flex: 1, maxWidth: 340 }}>
        <span style={{ color: "var(--faint)" }}>⌕</span>
        <input placeholder="Recherche plein texte…" style={{ border: 0, outline: "none", width: "100%", fontSize: 13, background: "transparent" }} />
      </div>
      <span style={{ flex: 1 }} />
      {exportable && <button className="btn btn-ghost">Exporter en CSV</button>}
      {ajouter && <button className="btn btn-primary" onClick={onAjouter}>{ajouter}</button>}
    </div>
  );
}

export interface ListeProps {
  colonnes: readonly string[];
  lignes: readonly (readonly Cellule[])[];
  hint?: string;
  ajouter?: string;
  exportable?: boolean;
  mode?: "normal" | "vide" | "erreur";
  extra?: ReactNode;
}

export function ListeAdmin({ colonnes, lignes, hint, ajouter, exportable, mode = "normal", extra }: ListeProps): React.ReactElement {
  const { chemin, naviguer } = useRoute();
  const detaillable = ajouter !== undefined;
  const ouvrirAjout = (): void => naviguer(`${chemin}/nouveau`);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <BarreFiltre ajouter={ajouter} exportable={exportable} onAjouter={ouvrirAjout} />
      {hint && <p style={{ margin: 0, fontSize: 12.5, color: "var(--muted-2)", maxWidth: "88ch" }}>{hint}</p>}
      {mode === "vide" && <EtatVide ajouter={ajouter} onAjouter={ouvrirAjout} />}
      {mode === "erreur" && <EtatErreur />}
      {mode === "normal" && (
        <div className="card" style={{ overflow: "hidden" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 13, padding: "11px 16px", borderBottom: "1px solid var(--line)", background: "var(--surface-2)", fontSize: 12.5, color: "var(--muted)" }}>
            <input type="checkbox" />
            <span><strong style={{ color: "var(--ink)" }}>{lignes.length}</strong> éléments</span>
            <span style={{ color: "var(--line-2)" }}>|</span>
            <button style={{ border: 0, background: "transparent", cursor: "pointer", color: "var(--muted)", fontSize: 12.5, fontWeight: 600, padding: 0 }}>Action groupée…</button>
          </div>
          <div className="scroll-x">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 34 }} />
                  {colonnes.map((c) => <th key={c} style={{ whiteSpace: "nowrap" }}>{c} <span style={{ color: "var(--line-2)" }}>↕</span></th>)}
                </tr>
              </thead>
              <tbody>
                {lignes.map((ligne, i) => (
                  <tr key={i} onClick={detaillable ? () => naviguer(`${chemin}/${i}`) : undefined} style={{ cursor: detaillable ? "pointer" : "default" }}>
                    <td style={{ width: 34 }} onClick={(e) => e.stopPropagation()}><input type="checkbox" /></td>
                    {ligne.map((c, j) => <td key={j}><Cell c={c} /></td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {extra}
    </div>
  );
}

function EtatVide({ ajouter, onAjouter }: { ajouter?: string; onAjouter?: () => void }): React.ReactElement {
  return (
    <div className="card" style={{ padding: "56px 24px", display: "flex", flexDirection: "column", alignItems: "center", gap: 11, textAlign: "center" }}>
      <span style={{ width: 44, height: 44, borderRadius: 12, background: "var(--panel)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 19, color: "var(--faint)" }}>⊘</span>
      <h3 className="display" style={{ margin: 0, fontSize: 18 }}>Aucun élément pour cette édition</h3>
      <p style={{ margin: 0, fontSize: 13.5, color: "var(--muted)", maxWidth: "44ch" }}>Changez d'édition dans le filtre, ou créez le premier enregistrement. Les éditions anciennes affichent « N/C » quand la donnée n'a jamais été saisie.</p>
      {ajouter && <button className="btn btn-primary" style={{ marginTop: 5 }} onClick={onAjouter}>{ajouter}</button>}
    </div>
  );
}

function EtatErreur(): React.ReactElement {
  return (
    <div className="card" style={{ padding: "40px 24px", display: "flex", flexDirection: "column", alignItems: "center", gap: 11, textAlign: "center", borderColor: "#F0CBC5" }}>
      <span style={{ width: 44, height: 44, borderRadius: 12, background: "var(--danger-bg)", color: "var(--danger)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, fontWeight: 800 }}>!</span>
      <h3 className="display" style={{ margin: 0, fontSize: 18 }}>La liste n'a pas pu être chargée</h3>
      <p style={{ margin: 0, fontSize: 13.5, color: "var(--muted)", maxWidth: "46ch" }}>Le service a répondu en erreur. Vos données ne sont pas perdues — réessayez dans un instant.</p>
      <button className="btn btn-ghost" style={{ marginTop: 5 }}>Réessayer</button>
    </div>
  );
}

// ---------- Formulaire ----------

export interface Champ {
  label: string;
  nom?: string; // clé dans `donnees` (défaut : label)
  type?: "texte" | "select" | "date" | "zone" | "check" | "fichier" | "couleur";
  span?: 1 | 2;
  hint?: string;
  requis?: boolean;
  options?: readonly string[];
  placeholder?: string;
  valeur?: string;
}
export interface Bloc {
  titre: string;
  finder?: boolean;
  champs: readonly Champ[];
}
export interface FormProps {
  intro?: string;
  banniere?: ReactNode;
  choix?: { label: string; options: readonly string[] };
  blocs: readonly Bloc[];
  submit: string;
  note?: string;
  /** Quand présent, la soumission enregistre vraiment ; sinon, note « maquette ». */
  onSubmit?: (valeurs: Record<string, string>) => Promise<void> | void;
}

const cleChamp = (f: Champ): string => f.nom ?? f.label;

export function FormulaireAdmin({ intro, banniere, choix, blocs, submit, note, onSubmit }: FormProps): React.ReactElement {
  const [valeurs, setValeurs] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const b of blocs) for (const c of b.champs) if (c.valeur !== undefined) init[cleChamp(c)] = c.valeur;
    return init;
  });
  const [etat, setEtat] = useState<"repos" | "maquette" | "envoi" | "ok" | "erreur">("repos");
  const [message, setMessage] = useState("");
  const set = (nom: string, v: string): void => setValeurs((s) => ({ ...s, [nom]: v }));

  async function soumettre(): Promise<void> {
    if (!onSubmit) { setEtat("maquette"); return; }
    setEtat("envoi");
    try { await onSubmit(valeurs); setEtat("ok"); }
    catch (e) { setEtat("erreur"); setMessage(e instanceof Error ? e.message : "Enregistrement impossible."); }
  }

  return (
    <div style={{ maxWidth: 900, display: "flex", flexDirection: "column", gap: 16 }}>
      {banniere}
      {intro && <p style={{ margin: 0, fontSize: 14, color: "var(--muted)", maxWidth: "70ch" }}>{intro}</p>}
      {choix && (
        <div className="card" style={{ padding: 18, display: "flex", flexDirection: "column", gap: 11 }}>
          <span className="eyebrow" style={{ color: "var(--faint)" }}>{choix.label}</span>
          <div style={{ display: "flex", gap: 11, flexWrap: "wrap" }}>
            {choix.options.map((o) => (
              <button key={o} className="btn btn-ghost" style={{ flex: 1, justifyContent: "flex-start", padding: "14px 15px", fontSize: 14, minWidth: 160 }}>{o}</button>
            ))}
          </div>
        </div>
      )}
      {blocs.map((b) => (
        <div key={b.titre} className="card" style={{ overflow: "hidden" }}>
          <div style={{ padding: "13px 20px", borderBottom: "1px solid var(--line)", background: "var(--surface-2)" }}>
            <h3 className="display" style={{ margin: 0, fontSize: 16 }}>{b.titre}</h3>
          </div>
          <div style={{ padding: 20 }}>
            {b.finder && (
              <div style={{ marginBottom: 18, padding: 15, border: "1px dashed var(--accent)", borderRadius: 11, background: "var(--soft)", display: "flex", flexDirection: "column", gap: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: "var(--accent)" }}>Rechercher un établissement de santé</span>
                <div style={{ display: "flex", alignItems: "center", gap: 9, background: "var(--surface)", border: "1px solid var(--line-2)", borderRadius: 9, padding: "10px 12px" }}>
                  <span style={{ color: "var(--faint)" }}>⌕</span>
                  <input placeholder="Tapez les premières lettres du nom, de la ville ou du code postal…" style={{ border: 0, outline: "none", width: "100%", fontSize: 13.5, background: "transparent" }} />
                </div>
                <span style={{ fontSize: 11.5, color: "var(--muted)", lineHeight: 1.5 }}>Le référentiel national pré-remplit le nom, l'adresse, le code postal et la ville. Établissement introuvable ? Renseignez les champs à la main ci-dessous.</span>
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 18px" }}>
              {b.champs.map((f) => <ChampVue key={cleChamp(f)} f={f} value={valeurs[cleChamp(f)] ?? ""} onChange={(v) => set(cleChamp(f), v)} />)}
            </div>
          </div>
        </div>
      ))}
      <div className="card" style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 18px", flexWrap: "wrap" }}>
        <button className="btn btn-primary" style={{ padding: "12px 20px", fontSize: 14 }} disabled={etat === "envoi"} onClick={() => void soumettre()}>{etat === "envoi" ? "Enregistrement…" : submit}</button>
        {etat === "ok" && <span className="pill pill-ok" style={{ textTransform: "none", fontFamily: "var(--sans)", fontSize: 12.5 }}>Enregistré ✓</span>}
        {etat === "erreur" && <span className="pill pill-danger" style={{ textTransform: "none", fontFamily: "var(--sans)", fontSize: 12.5 }}>{message}</span>}
        {etat === "maquette" && <span className="pill pill-warn" style={{ textTransform: "none", fontFamily: "var(--sans)", fontSize: 12.5 }}>Maquette : enregistrement non branché pour cette entité.</span>}
        {etat === "repos" && note && <span style={{ fontSize: 12, color: "var(--faint)", lineHeight: 1.5, flex: 1 }}>{note}</span>}
      </div>
    </div>
  );
}

function ChampVue({ f, value, onChange }: { f: Champ; value: string; onChange: (v: string) => void }): React.ReactElement {
  const span = f.span === 2 ? "1 / span 2" : "auto";
  if (f.type === "check") {
    return (
      <label style={{ gridColumn: span, display: "flex", alignItems: "center", gap: 10, fontSize: 13.5, color: "var(--ink-2)", cursor: "pointer" }}>
        <input type="checkbox" checked={value === "1"} onChange={(e) => onChange(e.target.checked ? "1" : "")} />{f.label}
      </label>
    );
  }
  return (
    <div className="field" style={{ gridColumn: span }}>
      <label>{f.label}{f.requis && <span style={{ color: "var(--danger)" }}> *</span>}</label>
      {f.type === "select" ? (
        <select className="select" value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">—</option>
          {(f.options ?? []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : f.type === "zone" ? (
        <textarea className="textarea" rows={3} placeholder={f.placeholder} value={value} onChange={(e) => onChange(e.target.value)} />
      ) : f.type === "date" ? (
        <input className="input" type="date" value={value} onChange={(e) => onChange(e.target.value)} />
      ) : f.type === "couleur" ? (
        <input type="color" value={value || "#2C3178"} onChange={(e) => onChange(e.target.value)} style={{ width: 64, height: 38, border: "1px solid var(--line-2)", borderRadius: 9, background: "var(--surface)", cursor: "pointer" }} />
      ) : f.type === "fichier" ? (
        <div style={{ border: "1px dashed var(--line-2)", borderRadius: 9, padding: "14px 12px", fontSize: 12.5, color: "var(--muted)", textAlign: "center", cursor: "pointer" }}>Déposer un fichier ou parcourir…</div>
      ) : (
        <input className="input" placeholder={f.placeholder} value={value} onChange={(e) => onChange(e.target.value)} />
      )}
      {f.hint && <span className="hint">{f.hint}</span>}
    </div>
  );
}

// ---------- Widgets / sections ----------

export function Widget({ titre, valeur, note, ton = "muted" }: { titre: string; valeur: string; note?: string; ton?: Ton }): React.ReactElement {
  return (
    <div className="card" style={{ padding: 16, display: "flex", flexDirection: "column", gap: 6 }}>
      <span className="eyebrow">{titre}</span>
      <span className="display" style={{ fontSize: 24 }}>{valeur}</span>
      {note && <span className={`pill pill-${ton}`} style={{ alignSelf: "flex-start", textTransform: "none", fontFamily: "var(--sans)", fontSize: 11.5 }}>{note}</span>}
    </div>
  );
}

export function Panneau({ titre, sous, children }: { titre: string; sous?: string; children: ReactNode }): React.ReactElement {
  return (
    <div className="card" style={{ padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>
      <div>
        <h3 className="display" style={{ margin: 0, fontSize: 16 }}>{titre}</h3>
        {sous && <p style={{ margin: "2px 0 0", fontSize: 12.5, color: "var(--muted-2)" }}>{sous}</p>}
      </div>
      {children}
    </div>
  );
}
